import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import MoreButtonArticle from '../../MoreButton/MoreButtonArticle';
import { generateArticleSlug } from '../../../i18n/utils/slugGenerator';
// import SocialShare from '../../SocialShare/SocialShare';
import ContentRenderer from '../ArticleRenderer/ContentRenderer';
import ArticlePageComponentSkeleton from './ArticlePageComponentSkeleton';
import ErrorPage from '../../../pages/404Page';
import SEO from '../../SEO/SEO';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../../config/constants';
import './ArticlePageComponent.scss';

const ArticlePageComponent = ({ hideHeaderFooterSetter, hideHeaderFooter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const location = useLocation();
    const currentUrl = window.location.origin + location.pathname;

    const { categorySlug, articleTitle } = useParams();
    const navigate = useNavigate();

    const { data: mainData, loading: mainDataLoading, error: mainDataError } = useCachedFetch('/data/locales/mainData.json');
    const { data: articlesData, loading: articlesLoading, error: articlesError } = useCachedFetch('/data/locales/articles.json');

    const [currentArticle, setCurrentArticle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);

    const mainContent = useMemo(() => {
        return mainData?.TopMainData?.ArticlePage;
    }, [mainData]);

    const articles = useMemo(() => {
        return articlesData?.ArticleData || [];
    }, [articlesData]);

    const findCurrentArticle = useCallback(() => {
        if (!categorySlug || !articleTitle || articles.length === 0) return null;

        return articles.find(
            (art) =>
                art.webLink.category.slug === categorySlug &&
                generateArticleSlug(art.mainArticleTitle, currentLang) === articleTitle
        );
    }, [categorySlug, articleTitle, articles, currentLang]);

    const getLocalizedTitle = useCallback((titleObj) => {
        return typeof titleObj === 'object'
            ? titleObj[currentLang] || titleObj.en
            : titleObj;
    }, [currentLang]);

    const getLocalizedContent = useCallback((content) => {
        return typeof content === 'object'
            ? content[currentLang] || content.en
            : content;
    }, [currentLang]);

    useEffect(() => {
        const loadAndFindArticle = async () => {
            try {
                setIsLoading(true);

                if (mainDataLoading || articlesLoading) return;

                if (mainDataError || articlesError) {
                    setHasData(false);
                    setIsLoading(false);
                    return;
                }

                if (!mainContent || !mainContent.button || !mainContent.buttonBlog || !mainContent.buttonsNav) {
                    setHasData(false);
                    setIsLoading(false);
                    return;
                }

                const article = findCurrentArticle();

                if (!article) {
                    setCurrentArticle(null);
                    setHasData(false);
                    setIsLoading(false);
                    return;
                }

                if (article && article.mainArticleTitle && article.articleImage && article.contentArticle && article.writer) {
                    setCurrentArticle(article);
                    setHasData(true);
                } else {
                    setCurrentArticle(null);
                    setHasData(false);
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Error in article loading:', error);
                setHasData(false);
                setIsLoading(false);
            }
        };

        loadAndFindArticle();
    }, [
        mainDataLoading, articlesLoading, mainDataError, articlesError,
        mainContent, findCurrentArticle
    ]);

    const articleSeoData = useMemo(() => {
        if (!currentArticle || !hasData) return null;

        const articleTitleText = getLocalizedTitle(currentArticle.mainArticleTitle);

        const firstParagraph = currentArticle.contentArticle?.blocks?.find(
            block => block.type === 'paragraph'
        );

        const description = firstParagraph?.content
            ? getLocalizedContent(firstParagraph.content)
            : articleTitleText;

        const fullImageUrl = currentArticle.articleImage?.startsWith('http')
            ? currentArticle.articleImage
            : `${IMAGES_BASE_URL}${currentArticle.articleImage}`;

        const tags = currentArticle.tags || [];

        return {
            title: articleTitleText,
            description: description,
            image: fullImageUrl,
            publishedTime: currentArticle.isoDate,
            modifiedTime: currentArticle.isoDate,
            author: getLocalizedTitle(currentArticle.writer?.authorArticle) || 'Carabinex Team',
            section: getLocalizedTitle(currentArticle.webLink?.category?.categoryName),
            tags: tags,
            url: currentUrl,
            type: 'article'
        };
    }, [currentArticle, hasData, currentUrl, getLocalizedTitle, getLocalizedContent]);

    const { nextArticle, previousArticle } = useMemo(() => {
        if (!currentArticle || articles.length === 0) {
            return { nextArticle: null, previousArticle: null };
        }

        const currentArticleIndex = articles.findIndex(art => art.id === currentArticle.id);
        const nextArticleIndex = (currentArticleIndex + 1) % articles.length;
        const previousArticleIndex = (currentArticleIndex - 1 + articles.length) % articles.length;

        return {
            nextArticle: articles[nextArticleIndex],
            previousArticle: articles[previousArticleIndex]
        };
    }, [currentArticle, articles]);

    const handleNextArticle = useCallback(() => {
        if (nextArticle) {
            navigate(`/blog/${nextArticle.webLink.category.slug}/${generateArticleSlug(nextArticle.mainArticleTitle, currentLang)}`);
        }
    }, [nextArticle, navigate, currentLang]);

    const handlePreviousArticle = useCallback(() => {
        if (previousArticle) {
            navigate(`/blog/${previousArticle.webLink.category.slug}/${generateArticleSlug(previousArticle.mainArticleTitle, currentLang)}`);
        }
    }, [previousArticle, navigate, currentLang]);

    const tableOfContents = useMemo(() => {
        if (!currentArticle?.contentArticle?.blocks) return [];

        const subtitleBlocksWithIndex = currentArticle.contentArticle.blocks
            .map((block, globalIndex) => ({ block, globalIndex }))
            .filter(({ block }) => block.type === 'subtitle');

        return subtitleBlocksWithIndex.map(({ block, globalIndex }) => {
            const content = typeof block.content === 'object'
                ? block.content[currentLang] || block.content.en
                : block.content;

            return (
                <a
                    key={globalIndex}
                    className="article__link"
                    href={`#subtitle-${globalIndex}`}
                    onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(`subtitle-${globalIndex}`);
                        if (element) {
                            const yOffset = -100;
                            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                    }}
                >
                    <h4 className="article__link__title">{content}</h4>
                </a>
            );
        });
    }, [currentArticle, currentLang]);

    // if (isLoading && !currentArticle) {
    //     return <ArticlePageComponentSkeleton />;
    // }

    // if (!isLoading && !currentArticle) {
    //     return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    // }

    // if (!currentArticle && !isLoading) {
    //     return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    // }

    // if (!hasData || !mainContent) {
    //     return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    // }

    // if (!currentArticle) {
    //     return <ArticlePageComponentSkeleton />; // Продолжаем показывать скелетон вместо резкой ошибки
    // }

        if (isLoading && !currentArticle) {
        return <ArticlePageComponentSkeleton />;
    }

    if (!isLoading && !currentArticle) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    }

    if (!currentArticle && !isLoading) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    }

    if (!hasData || !mainContent) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} hideHeaderFooter={hideHeaderFooter} />;
    }

    if (!currentArticle) {
        return <ArticlePageComponentSkeleton />;
    }

    return (
        <>
            {articleSeoData && (
                <SEO
                    title={articleSeoData.title}
                    description={articleSeoData.description}
                    image={articleSeoData.image}
                    url={articleSeoData.url}
                    type={articleSeoData.type}
                    publishedTime={articleSeoData.publishedTime}
                    modifiedTime={articleSeoData.modifiedTime}
                    author={articleSeoData.author}
                    section={articleSeoData.section}
                    tags={articleSeoData.tags}
                />
            )}

            <div className='article__wrapper'>
                <div className='all__link'>
                    <Link to={mainContent.buttonBlog.link} className='all__link__text'>
                        {mainContent.buttonBlog.text?.[currentLang] || mainContent.buttonBlog.text?.en || ''}
                        <img src={`${IMAGES_BASE_URL}${mainContent.buttonBlog.image}`} alt="all-articles-icon" className="all__link__icon" loading="lazy" />
                    </Link>
                </div>
                <div className='all__route'>
                    {nextArticle && (
                        <button onClick={handleNextArticle} className="all__link__next">
                            {mainContent.buttonsNav.text1?.[currentLang] || mainContent.buttonsNav.text1?.en || ''}{' '}
                            <FontAwesomeIcon className="all__arrow__next" icon={faChevronDown} />
                        </button>
                    )}
                    {previousArticle && (
                        <button onClick={handlePreviousArticle} className="all__link__prev">
                            {mainContent.buttonsNav.text2?.[currentLang] || mainContent.buttonsNav.text2?.en || ''}{' '}
                            <FontAwesomeIcon className="all__arrow__prev" icon={faChevronDown} />
                        </button>
                    )}
                </div>
                <div className='article__info__wrapper'>
                    <div
                        className='image__article__info__item'
                        style={{ backgroundImage: `url(${IMAGES_BASE_URL}${currentArticle.articleImage})` }}
                    >
                        <div className='recent__work__substrate__gradient'></div>
                        <div className='image__article__info__item__cont'>
                            <AnimatedOnScroll animation="fade-down" delay={0}>
                                <h2 className='image__article__info__item__title'>
                                    {getLocalizedTitle(currentArticle.mainArticleTitle)}
                                </h2>
                            </AnimatedOnScroll>
                            <p className='image__article__info__item__par'>
                                {typeof currentArticle.dateArticle === 'object'
                                    ? currentArticle.dateArticle[currentLang] || currentArticle.dateArticle.en
                                    : currentArticle.dateArticle
                                } {mainContent.in?.[currentLang] || mainContent.in?.en || ''}
                                <Link to={`/blog/${currentArticle.webLink.category.slug}`} className="image__article__info__item__link">
                                    {typeof currentArticle.webLink.category.categoryName === 'object'
                                        ? currentArticle.webLink.category.categoryName[currentLang] || currentArticle.webLink.category.categoryName.en
                                        : currentArticle.webLink.category.categoryName
                                    }
                                </Link>
                            </p>
                            <MoreButtonArticle />
                        </div>
                    </div>
                    <div className='author__article__info__item'>
                        <div className='author__article__info__item__content__side'>
                            <div className='author__article__info__item__content__side__image'>
                                <img className="article__image__pic" src={`${IMAGES_BASE_URL}${currentArticle.writer.photo}`} alt='Author' loading="lazy" />
                            </div>
                            <div className='author__article__info__item__content__side__text'>
                                <h3 className='author__name'>
                                    {typeof currentArticle.writer.authorArticle === 'object'
                                        ? currentArticle.writer.authorArticle[currentLang] || currentArticle.writer.authorArticle.en
                                        : currentArticle.writer.authorArticle
                                    } <span className="author__position"> - {typeof currentArticle.writer.position === 'object'
                                        ? currentArticle.writer.position[currentLang] || currentArticle.writer.position.en
                                        : currentArticle.writer.position
                                    },</span>
                                    <a href={currentArticle.writer.companyLink} className='company__name' target="_blank"
                                        rel="noopener noreferrer">
                                        {typeof currentArticle.writer.company === 'object' && currentArticle.writer.company[currentLang]
                                            ? currentArticle.writer.company[currentLang]
                                            : typeof currentArticle.writer.company === 'string' && currentArticle.writer.company
                                                ? currentArticle.writer.company
                                                : ''
                                        }
                                    </a>
                                </h3>
                                <p className='author__description'>
                                    {typeof currentArticle.writer.description === 'object'
                                        ? currentArticle.writer.description[currentLang] || currentArticle.writer.description.en
                                        : currentArticle.writer.description
                                    }
                                </p>
                            </div>
                        </div>

                        {/* <div className='author__article__info__item__links__side'>
                            <div className='author__article__info__item__share__side__wrapper'>
                                <h3 className='share__title'>{mainContent.share?.[currentLang] || mainContent.share?.en || ''}</h3>
                            </div>
                            <div className='author__article__info__item__links__side__wrapper'>
                                <SocialShare
                                    article={currentArticle}
                                    currentUrl={currentUrl}
                                />
                            </div>
                        </div> */}
                    </div>
                </div>

                <div className="articles__wrapper">
                    <div className="articles">
                        <div key={currentArticle.id} className="article">
                            <ContentRenderer
                                blocks={currentArticle.contentArticle.blocks}
                                currentLang={currentLang}
                            />

                            {/* <div className="article__rating__container">
                                <div className='author__article__info__item__links__side'>
                                    <div className='author__article__info__item__share__side__wrapper'>
                                        <h3 className='share__title'>{mainContent.share?.[currentLang] || mainContent.share?.en || ''}</h3>
                                    </div>
                                    <div className='author__article__info__item__links__side__wrapper'>
                                        <SocialShare
                                            article={currentArticle}
                                            currentUrl={currentUrl}
                                        />
                                    </div>
                                </div>
                            </div> */}
                        </div>
                    </div>
                    <div className='article__toc__item__wrapper'>
                        <div className="article__toc">
                            <h5 className='article__toc__title'>{mainContent.inThisArticle?.[currentLang] || mainContent.inThisArticle?.en || ''}</h5>
                            <div className="toc__article">
                                {tableOfContents}
                            </div>
                        </div>
                        <div className='article__quote'>
                            <p className='article__quote__accent'>{mainContent.contentAccent?.[currentLang] || mainContent.contentAccent?.en || ''}</p>
                            <h3 className='article__quote__subtitle'>{mainContent.contentSubtitle?.[currentLang] || mainContent.contentSubtitle?.en || ''}</h3>
                            <Link to={mainContent.button.link} className='article__quote__button'>
                                <span></span>
                                <span></span>
                                <span></span>
                                <span></span>
                                {mainContent.button.text?.[currentLang] || mainContent.button.text?.en || ''}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            {nextArticle && (
                <div className='next__article' style={{ backgroundImage: `url(${IMAGES_BASE_URL}${nextArticle.articleImage})` }}>
                    <div className='next__article__gradient'></div>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                        <p className='next__article__subtitle'>{mainContent.nextArticle?.[currentLang] || mainContent.nextArticle?.en || ''}</p>
                    </AnimatedOnScroll>
                    <button
                        className="next__article__link"
                        onClick={handleNextArticle}
                    >
                        <AnimatedOnScroll animation="fade-up" delay={100}>
                            <h2 className="next__article__title">
                                {getLocalizedTitle(nextArticle.mainArticleTitle)}
                                <FontAwesomeIcon className="article__arrow" icon={faChevronDown} />
                            </h2>
                        </AnimatedOnScroll>
                    </button>
                </div>
            )}
        </>
    );
};

export default React.memo(ArticlePageComponent);